//BusinessUtilities.js
import axios from 'axios';
import { api } from '../../api'; // Adjust the import path as necessary

// 0) Acquire as a key value pair the business and the token of all in the database
export async function fetchBusinessTokenPairs() {
  const response = await api.get(`/business/business-token-pairs`);
  return response.data;
}

// 1) Acquire the business based on a given token
export async function fetchBusinessByEmail(email) {
  const response = await api.get(`/business/businesses?email=${email}`);
  return response.data;
}

// 2) Acquire all device ids that have a given token
export async function fetchDeviceIdsByToken(store_id) {
  const response = await api.get(`/business/devices?store_id=${store_id}`);
  return response.data;
}

export async function fetchStoresByBusinessId(business_id) {
  const response = await api.get(`/business/stores?business_id=${business_id}`);
  return response.data;
}

// NEW: Fetch stores with enhanced availability data from StoresData table
export async function fetchStoresWithAvailability(business_id) {
  const response = await api.get(`/business/stores-with-availability/${business_id}`);
  return response.data;
}

// NEW: Get business summary data including health scores and activity metrics
export async function fetchStoresDataSummary(business_id) {
  const response = await api.get(`/business/stores-data-summary/${business_id}`);
  return response.data;
}

// 3) A function that returns all lines inserted from all device ids attached to a token in the last week
export async function fetchLinesByToken(business_id) {
  const response = await api.get(`/business/lines?business_id=${business_id}`);
  return response.data.length;
}

export async function fetchLinesLastWeek(business_id){
  const response = await api.get(`/business/lines-last?business_id=${business_id}`);
  return response.data.length;
}

export async function fetchBusinessViewDataByToken(business_id) {
  const response = await api.get(`/business/businessDataView?business_id=${business_id}&weeks=5`);
  return response.data;
}

export async function getIndustryAverage(industry) {
  const response = await api.get(`/business/industryAverage?industry=${industry}`);
  return response.data;
}

export async function getIndustryByToken(business_id) {
  const response = await api.get(`/business/industry?business_id=${business_id}`);
  return response.data;
}

// NEW: Enhanced store data fetching with smart loading capabilities
export async function fetchStoreDataSmart(business_id, options = {}) {
  try {
    // Try the enhanced StoresData endpoint first
    const storesWithAvailability = await fetchStoresWithAvailability(business_id);
    
    if (storesWithAvailability && storesWithAvailability.length > 0) {
      return {
        stores: storesWithAvailability,
        enhanced: true,
        summary: options.includeSummary ? await fetchStoresDataSummary(business_id) : null
      };
    }
  } catch (error) {
    console.warn('Enhanced stores data not available, falling back to basic stores:', error);
  }
  
  // Fallback to basic stores endpoint
  const basicStores = await fetchStoresByBusinessId(business_id);
  return {
    stores: basicStores,
    enhanced: false,
    summary: null
  };
}

// NEW: Function to get recommended store for a business (if you have this endpoint)
export async function fetchRecommendedStore(business_id) {
  try {
    const response = await api.get(`/business/recommended-store/${business_id}`);
    return response.data;
  } catch (error) {
    console.warn('Recommended store endpoint not available:', error);
    return null;
  }
}

// NEW: Function to trigger real-time store data update
export async function updateSpecificStoreData(store_id) {
  try {
    const response = await api.post('/business/update-specific-store', { store_id });
    return response.data;
  } catch (error) {
    console.error('Failed to trigger store data update:', error);
    throw error;
  }
}

// NEW: Helper function to determine best store selection strategy
export function selectBestStore(storesData, strategy = 'auto') {
  if (!storesData || storesData.length === 0) {
    return null;
  }

  switch (strategy) {
    case 'most_active_today':
      return storesData
        .filter(store => store.has_data_today)
        .sort((a, b) => (b.responses_today || 0) - (a.responses_today || 0))[0];
    
    case 'highest_quality':
      return storesData
        .sort((a, b) => (b.computed_quality_score || 0) - (a.computed_quality_score || 0))[0];
    
    case 'most_recent_activity':
      return storesData
        .filter(store => store.has_recent_data)
        .sort((a, b) => new Date(b.last_response_date || 0) - new Date(a.last_response_date || 0))[0];
    
    case 'auto':
    default:
      // Auto strategy: prioritize data today > quality score > responses today
      return storesData.sort((a, b) => {
        // Primary: has data today
        if (a.has_data_today !== b.has_data_today) {
          return b.has_data_today - a.has_data_today;
        }
        // Secondary: computed quality score
        if (a.computed_quality_score !== b.computed_quality_score) {
          return (b.computed_quality_score || 0) - (a.computed_quality_score || 0);
        }
        // Tertiary: responses today
        return (b.responses_today || 0) - (a.responses_today || 0);
      })[0];
  }
}

// NEW: Helper function to get store health summary
export function getStoreHealthSummary(storesData) {
  if (!storesData || storesData.length === 0) {
    return {
      totalStores: 0,
      activeToday: 0,
      activeThisWeek: 0,
      needsAttention: 0,
      avgQualityScore: 0,
      healthStatus: 'unknown'
    };
  }

  const totalStores = storesData.length;
  const activeToday = storesData.filter(store => store.has_data_today).length;
  const activeThisWeek = storesData.filter(store => store.has_data_this_week).length;
  const needsAttention = storesData.filter(store => store.needsAttention).length;
  const avgQualityScore = storesData.reduce((sum, store) => sum + (store.computed_quality_score || 0), 0) / totalStores;

  let healthStatus = 'poor';
  if (avgQualityScore > 0.7 && activeToday / totalStores > 0.5) {
    healthStatus = 'excellent';
  } else if (avgQualityScore > 0.5 && activeThisWeek / totalStores > 0.3) {
    healthStatus = 'good';
  } else if (avgQualityScore > 0.3 || activeThisWeek / totalStores > 0.2) {
    healthStatus = 'fair';
  }

  return {
    totalStores,
    activeToday,
    activeThisWeek,
    needsAttention,
    avgQualityScore: Math.round(avgQualityScore * 100) / 100,
    healthStatus,
    activityRate: Math.round((activeThisWeek / totalStores) * 100),
    todayActivityRate: Math.round((activeToday / totalStores) * 100)
  };
}